// MainPage.tsx

import { useEffect, useMemo, useState } from "react";
import { BottomDrawer } from "@bridge-applications/ui-kit";
import "./MainPage.scss";
import MainTitle from "./components/MainTitle/MainTitle";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../common/LoadingScreen/LoadingScreen";
import { Toaster } from "react-hot-toast";
import { z } from "zod";
import {
    FeedItemSchema,
    ApiSurveyStartReqSchema,
    ApiSurveyStartResSchema,
} from "@bridge-applications/eh-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { API_BASE, CLIENT_VERSION, NS2 } from "../../../state/config";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import TaskList, { TaskListItemSchema } from "./components/TaskList/TaskList";
import CloudBlobsOverlay from "./components/CloudBlobsOverlay/CloudBlobsOverlay";
import XLogoSvg from "../../../assets/icons/x-logo-svg.svg?react";
import TaskOverviewSurvey from "../../common/BottomDrawer/contents/TaskOverviewSurvey/TaskOverviewSurvey";
import TaskOverViewCheckInCampaign from "../../common/BottomDrawer/contents/TaskOverViewCheckInCampaign/TaskOverViewCheckInCampaign";
import TaskOverviewActionCampaign from "../../common/BottomDrawer/contents/TaskOverviewActionCampaign/TaskOverviewActionCampaign";
import { useTranslation } from "react-i18next";
import RewardActivityTicker from "./components/RewardActivityTicker/RewardActivityTicker";
import { telemetry } from "@bridge-applications/telemetry-js";
import TaskListHeader2 from "./components/TaskListHeader2/TaskListHeader2";
import UserStatsTicker from "./components/UserStatsTicker/UserStatsTicker";
import Skeleton from "../../common/Skeleton/Skeleton";
import { sendHapticFeedback } from "../../../logic/helpers/world/sendHapticFeedback";
import { getGameCatalogueFeedItem } from "../../../logic/helpers/feed/getGameCatalogueFeedItem";
import { MiniKit } from "@worldcoin/minikit-js";
import type {
    CommandResultByVia,
    CloseMiniAppResult,
    MiniKitCloseMiniAppOptions,
} from "@worldcoin/minikit-js/commands";

type LanguageCode = "en" | "es" | "id" | "pt-br" | "th";

const LANG_TO_URL_SUFFIX: Record<LanguageCode, string> = {
    en: "en",
    es: "es",
    id: "id",
    "pt-br": "ptbr",
    th: "th",
};

const WBC_STW_CLAIM_POLL_KEY = "__earnHub__wbc_stw_claim_poll_v1";
const WBC_STW_CLAIM_POLL_TTL_MS = 15 * 60 * 1000;
const X_FOLLOW_BUTTON_ROLLOUT_KEY = `${NS2}x_follow_button_rollout_v1`;
const X_FOLLOW_BUTTON_ROLLOUT_RATIO = 1;

const shouldShowXFollowButton = () => {
    try {
        const storedBucket = localStorage.getItem(X_FOLLOW_BUTTON_ROLLOUT_KEY);
        const bucket =
            storedBucket === null ? Math.random() : Number(storedBucket);

        if (!Number.isFinite(bucket) || bucket < 0 || bucket >= 1) {
            const newBucket = Math.random();
            localStorage.setItem(
                X_FOLLOW_BUTTON_ROLLOUT_KEY,
                String(newBucket),
            );
            return newBucket < X_FOLLOW_BUTTON_ROLLOUT_RATIO;
        }

        if (storedBucket === null) {
            localStorage.setItem(X_FOLLOW_BUTTON_ROLLOUT_KEY, String(bucket));
        }

        return bucket < X_FOLLOW_BUTTON_ROLLOUT_RATIO;
    } catch {
        return false;
    }
};

export async function closeMiniApp() {
    const input = {} satisfies MiniKitCloseMiniAppOptions;

    const result: CommandResultByVia<
        CloseMiniAppResult,
        CloseMiniAppResult,
        "minikit"
    > = await MiniKit.closeMiniApp(input);

    return result.data.status;
}

export default function MainPage() {
    const { i18n, t } = useTranslation();
    const [openBottomDrawer, setOpenBottomDrawer] = useState<
        | "TaskOverviewSurvey"
        | "TaskOverViewCheckInCampaign"
        | "TaskOverviewAction"
        | null
    >(null);
    const { state: feedState } = useSlice("feedSlice");
    const { state: appSlice } = useSlice("appSlice");
    const [selectedTask, setSelectedTask] = useState<
        z.infer<typeof FeedItemSchema> | undefined
    >(undefined);
    type FeedItem = z.infer<typeof FeedItemSchema>;
    const navigate = useNavigate();
    const { showLoading, hideLoading } = useLoading();
    const [showXFollowButton] = useState(() => shouldShowXFollowButton());
    // --- LS "sub-product visited today" helpers
    const NS2_VISITS_KEY = `${NS2}subproduct_visits_v1`;
    const getLocalDateKey = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const getVisitedToday = (): Set<string> => {
        try {
            const raw = localStorage.getItem(NS2_VISITS_KEY);
            if (!raw) return new Set();
            const map: Record<string, string> = JSON.parse(raw);
            const today = getLocalDateKey();
            return new Set(
                Object.entries(map)
                    .filter(([, date]) => date === today)
                    .map(([id]) => id),
            );
        } catch {
            return new Set();
        }
    };
    const markVisited = (productId: string) => {
        try {
            const raw = localStorage.getItem(NS2_VISITS_KEY);
            const map: Record<string, string> = raw ? JSON.parse(raw) : {};
            map[productId] = getLocalDateKey();
            localStorage.setItem(NS2_VISITS_KEY, JSON.stringify(map));
        } catch {
            /* noop */
        }
    };
    const [visitedToday] = useState(() => getVisitedToday());
    const gameCatalogueFeedItem = useMemo(() => getGameCatalogueFeedItem(), []);

    // --- LS "resume drawer" helpers
    const RESUME_KEY = "eh_resume_bottom_drawer_v1";
    const RESUME_TTL_MS = 15 * 60 * 1000; // 15m
    type ResumePayload = {
        t: "chi" | "survey" | "action";
        taskId: string;
        at: number;
        ttlMs?: number;
    };
    const readResume = (): ResumePayload | null => {
        try {
            const raw = localStorage.getItem(RESUME_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as ResumePayload;
            const ttl =
                typeof parsed.ttlMs === "number" ? parsed.ttlMs : RESUME_TTL_MS;
            if (Date.now() - (parsed.at || 0) > ttl) {
                localStorage.removeItem(RESUME_KEY);
                return null;
            }
            return parsed;
        } catch {
            try {
                localStorage.removeItem(RESUME_KEY);
            } catch {
                console.error("Failed to remove resume item");
            }
            return null;
        }
    };
    const writeResume = (p: ResumePayload) => {
        try {
            localStorage.setItem(RESUME_KEY, JSON.stringify(p));
        } catch {
            console.error("Failed to write resume item");
        }
    };
    const clearResume = () => {
        try {
            localStorage.removeItem(RESUME_KEY);
        } catch {
            console.error("Failed to clear resume item");
        }
    };

    const handleTaskPreviewClick = (itemId: string) => {
        const item =
            itemId === gameCatalogueFeedItem.itemId
                ? gameCatalogueFeedItem
                : feedState.items.find((it) => it.itemId === itemId);
        if (!item) {
            console.error(
                `Feed item with id ${itemId} not found in the state.`,
            );
            return;
        }
        setSelectedTask(item);
        // Decide which bottom drawer to open
        if (item.productType === "SURVEY") {
            setOpenBottomDrawer("TaskOverviewSurvey");
            // Optional: only persist for survey flows if you also want resume there
            // writeResume({ t: "survey", taskId: itemId, at: Date.now(), ttlMs: RESUME_TTL_MS });
            return;
        }
        if (item.productType === "PARTNER_CAMPAIGN_CHI") {
            // Check-in campaigns are the only PARTNER type for now
            setOpenBottomDrawer("TaskOverViewCheckInCampaign");
            // Persist intent so a reload when returning from mini-app reopens the same drawer
            writeResume({
                t: "chi",
                taskId: itemId,
                at: Date.now(),
                ttlMs: RESUME_TTL_MS,
            });
            return;
        }
        if (item.productType === "PARTNER_CAMPAIGN_ACTION") {
            // One-shot action tasks (join Telegram bot, visit a URL, etc.)
            setOpenBottomDrawer("TaskOverviewAction");
            writeResume({
                t: "action",
                taskId: itemId,
                at: Date.now(),
                ttlMs: RESUME_TTL_MS,
            });
            return;
        }
        if (item.productType === "INTERNAL_CAMPAIGN_STW") {
            navigate("/spin", { state: { campaignId: itemId } });
            return;
        }
        if (item.productType === "WBC_CAMPAIGN_STW") {
            try {
                const now = Date.now();
                localStorage.setItem(
                    WBC_STW_CLAIM_POLL_KEY,
                    JSON.stringify({
                        type: "WBC_STW",
                        campaignId: item.raw.id,
                        internalCampaignId: item.raw.internalCampaignId,
                        startedAt: now,
                        pollUntil: now + WBC_STW_CLAIM_POLL_TTL_MS,
                    }),
                );
            } catch {
                console.error("Failed to write WBC STW claim poll intent");
            }
            window.open(item.wbc.launchUrl, "_blank");
            // WAIT FOR 1 SECOND
            // setTimeout(async () => {
            //     await closeMiniApp();
            // }, 1000);
            setOpenBottomDrawer(null);
            return;
        }
        if (item.productType === "GENERIC_LINK") {
            telemetry.capture("generic_link__nav-from-main-page", {
                item_id: item.itemId,
                link_type: item.link.type,
            });

            if (item.link.type === "INTERNAL_ROUTE" && item.link.path) {
                navigate(item.link.path);
            } else if (item.link.type === "EXTERNAL_URL" && item.link.url) {
                window.open(item.link.url, "_blank");
            } else if (item.link.type === "WORLD_APP" && item.link.appId) {
                const targetURL = `https://world.org/mini-app?app_id=${item.link.appId}&path=${encodeURIComponent(
                    item.link.path ?? "/",
                )}`;
                window.open(targetURL, "_blank");
            }

            setOpenBottomDrawer(null);
            return;
        }
        if (item.productType === "SCRATCH") {
            const sub: string = "app_443bd39e83f7ab076b200e630c70c772";
            const targetURL = `https://world.org/mini-app?app_id=${sub}&path=${encodeURIComponent(
                `/scratch?ref=eh`,
            )}`;
            window.open(targetURL, "_blank");
        }
        if (item.productType === "BRIDGE_CHECK_IN") {
            const sub: string = "app_443bd39e83f7ab076b200e630c70c772";
            const targetURL = `https://world.org/mini-app?app_id=${sub}&path=${encodeURIComponent(
                `/?ref=eh`,
            )}`;
            window.open(targetURL, "_blank");
        }
        if (item.productType === "PARTNER_LINK") {
            window.open(item.raw?.deeplinkUrl ?? "", "_blank");
        }
        if (item.productType === "INTERNAL_CAMPAIGN_REF") {
            navigate("/referral-v2", { state: { campaignId: itemId } });
        }
        if (item.productType === "AB_TEST") {
            navigate("/ab-page", { state: { campaignId: itemId } });
        }
        if (item.productType === "GAE") {
            telemetry.capture("gae__nav-from-main-page");
            navigate("/game", { state: { campaignId: itemId } });
        }

        // INTERNAL (e.g., SPIN_THE_WHEEL) – no drawer for now
        setOpenBottomDrawer(null);
    };

    // useEffect(() => {
    //     const params = new URLSearchParams(location.search);
    //     const ehId = params.get("eh-id");
    //     console.log("ehId", ehId);
    //     if (ehId && /^[a-zA-Z0-9]{8}$/.test(ehId)) {
    //         const partnerId: string = "3T9ap7dA";
    //         fetch(`https://api.eh.bridge.cm/api/v1/partner/check-in`, {
    //             method: "POST",
    //             body: JSON.stringify({ partnerId, ehId }),
    //             headers: { "Content-Type": "application/json" },
    //             credentials: "omit",
    //         });
    //     }
    // }, [location]);

    // Start handler (SURVEY only for now): calls /survey/start with listingToken, then navigates to /task
    const handleTaskStartClick = async (itemId: string) => {
        const item = feedState.items.find((it) => it.itemId === itemId);
        if (!item) {
            console.error(`Feed item ${itemId} not found`);
            return;
        }
        if (item.productType !== "SURVEY") {
            // Only surveys have a task flow here; other product types handle start elsewhere
            console.warn("Start is only implemented for SURVEY here.");
            return;
        }

        try {
            const { accessToken } =
                await WorldAuthClient.getAccessTokenSilently();

            // Build start payload (typed)
            const payload = ApiSurveyStartReqSchema.parse({
                surveyId: item.itemId,
                taskToken: item.listingToken, // added by server
                taskVersion: String((item as any)?.raw?.version ?? "1"),
                clientVersion: CLIENT_VERSION,
                renderedSpotsLeft: (item as any).spotsLeft ?? undefined,
            });

            const res = await fetch(`${API_BASE}/api/v1/survey/start`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload),
            });
            const json = await res.json();
            const parsed = ApiSurveyStartResSchema.safeParse(json);
            if (!parsed.success) {
                console.error("Invalid survey/start response", parsed.error);
                return;
            }

            // Prepare client task state for TaskPage (SurveySchema + tokens)
            const navState = {
                ...(item as any).raw, // SurveySchema
                taskToken: payload.taskToken,
                taskStartToken: parsed.data.surveyStartToken,
            };

            setOpenBottomDrawer(null);
            showLoading("task-env", () => {
                navigate(`/task/${itemId}`, { state: navState });
                setTimeout(hideLoading, 500);
            });
        } catch (e) {
            console.error("Failed to start survey", e);
        }
    };

    // Map a FeedItem to TaskListItem (what the card expects)
    const getTaskListItem = (
        item: FeedItem,
    ): z.infer<typeof TaskListItemSchema> => {
        let isPinned: boolean = false;

        const resolveLocalizedText = (
            text: unknown,
            fallback: string,
        ): string => {
            if (!text) return fallback;

            if (typeof text === "string") return text;

            if (typeof text === "object" && !Array.isArray(text)) {
                const localized = text as Partial<Record<LanguageCode, string>>;
                const currentLang = i18n.language as LanguageCode;
                return localized[currentLang] ?? localized.en ?? fallback;
            }

            return fallback;
        };

        const now = Date.now();
        const startsAt = item.startsAt
            ? new Date(item.startsAt).getTime()
            : null;
        const endsAt = item.endsAt ? new Date(item.endsAt).getTime() : null;
        const inWindow =
            (startsAt === null || startsAt <= now) &&
            (endsAt === null || endsAt > now);

        // relation from server (may be undefined)
        const rel: any = (item as any).relation;

        // Spin-the-wheel relation (for subtitle + actionability)
        let stwCanSpinNow: boolean | undefined = undefined;
        let stwCanSpinAt: number | undefined = undefined;
        if (
            item.productType === "INTERNAL_CAMPAIGN_STW" ||
            item.productType === "WBC_CAMPAIGN_STW"
        ) {
            if (rel) {
                stwCanSpinNow = !!rel.canSpinNow;
                stwCanSpinAt = rel.canSpinAt
                    ? new Date(rel.canSpinAt).getTime()
                    : undefined;
            }
        }

        // BRIDGE_CHECK_IN relation (countdown + actionability)
        let brCanCheckNow: boolean | undefined = undefined;
        let brNextEligibleAt: number | undefined = undefined;
        if (item.productType === "BRIDGE_CHECK_IN" && rel) {
            const ts =
                typeof rel.nextEligibleAtTs === "number"
                    ? rel.nextEligibleAtTs
                    : undefined;
            brNextEligibleAt = ts;
            brCanCheckNow =
                rel.canCheckInNow === true ||
                ts == null ||
                (typeof ts === "number" && ts <= now);

            // Debug logging for timezone issues
            // if (ts) {
            //     console.log("[BRIDGE_CHECK_IN Debug]", {
            //         nextEligibleAtTs: ts,
            //         nextEligibleAtDate: new Date(ts).toISOString(),
            //         nextEligibleAtLocal: new Date(ts).toLocaleString(),
            //         now: now,
            //         nowDate: new Date(now).toISOString(),
            //         nowLocal: new Date(now).toLocaleString(),
            //         differenceMs: ts - now,
            //         differenceMinutes: Math.round((ts - now) / 60000),
            //         canCheckInNow: rel.canCheckInNow,
            //         timezone: rel.timezone,
            //     });
            // }
        }

        // Can the user take action?
        // - SURVEY / CHECK_IN_CAMPAIGN: use canStart/canResume (if present)
        // - WHEEL_SPIN: actionable if can spin now or canSpinAt is in the past
        const isActionable =
            item.productType === "INTERNAL_CAMPAIGN_REF"
                ? true
                : item.productType === "INTERNAL_CAMPAIGN_STW"
                  ? !!stwCanSpinNow ||
                    (typeof stwCanSpinAt === "number" && stwCanSpinAt <= now)
                  : item.productType === "WBC_CAMPAIGN_STW"
                    ? !!stwCanSpinNow ||
                      (typeof stwCanSpinAt === "number" && stwCanSpinAt <= now)
                    : item.productType === "BRIDGE_CHECK_IN"
                      ? !!brCanCheckNow ||
                        (typeof brNextEligibleAt === "number" &&
                            brNextEligibleAt <= now)
                      : rel == null
                        ? true
                        : item.productType === "PARTNER_CAMPAIGN_CHI" &&
                            rel.hasFailed === true
                          ? false
                          : Boolean(rel.canStart || rel.canResume);

        // Map relation.status -> UI userRelation
        let userRelation: "not-started" | "in-progress" | "completed" =
            "not-started";
        if (rel?.status === "IN_PROGRESS") userRelation = "in-progress";
        else if (rel?.status === "COMPLETED") userRelation = "completed";

        console.log("PRODUCT:", item.productType, inWindow, isActionable);

        const taskStatus: z.infer<typeof TaskListItemSchema>["taskStatus"] =
            inWindow && isActionable ? "available" : "closed";

        // reward - use available properties
        const unit = item.rewardUnit ?? "USDC";
        const rewardAmount =
            item.rewardAmount != null ? Number(item.rewardAmount) : 0;

        // type mapping for the badge
        let listType: z.infer<typeof TaskListItemSchema>["type"] = "SURVEY";
        if (item.productType === "PARTNER_CAMPAIGN_CHI")
            listType = "CHECK_IN_CAMPAIGN";
        // Render one-shot action tasks with the reward-card styling for now.
        if (item.productType === "PARTNER_CAMPAIGN_ACTION")
            listType = "PARTNER_CAMPAIGN_ACTION";
        if (item.productType === "INTERNAL_CAMPAIGN_STW")
            listType = "WHEEL_SPIN";
        if (item.productType === "WBC_CAMPAIGN_STW") listType = "WHEEL_SPIN";
        if (item.productType === "BRIDGE_CHECK_IN")
            listType = "BRIDGE_CHECK_IN";
        if (item.productType === "INTERNAL_CAMPAIGN_REF") listType = "REFERRAL";
        if (item.productType === "PARTNER_LINK") {
            listType = "PARTNER_LINK";
            isPinned = true;
        }
        if (item.productType === "AB_TEST") {
            listType = "AB_TEST";
            // isPinned = true;
        }
        if (item.productType === "GAE") {
            listType = "GAE";
        }
        if (item.productType === "GENERIC_LINK") {
            listType = "GENERIC_LINK";
        }

        // (stwCanSpinNow / stwCanSpinAt already computed above)

        let estCompletionTime = 0;
        if (
            item.productType === "SURVEY" ||
            item.productType === "PARTNER_CAMPAIGN_CHI" ||
            item.productType === "PARTNER_CAMPAIGN_ACTION"
        ) {
            estCompletionTime = item.raw.est_completion_time ?? 0;
        }

        return {
            id: item.itemId,
            type: listType,
            title: resolveLocalizedText(item.title, "Untitled"),
            subtitle: resolveLocalizedText(item.subtitle, ""),
            imgUrl: item.thumbnailUrl ?? "",
            duration: estCompletionTime,
            spotsLeft: (item as any).spotsLeft ?? 0,
            createdAt: item.createdAt
                ? new Date(item.createdAt).getTime()
                : undefined,
            primaryReward: {
                type: "TOKEN",
                unit:
                    unit === "WLD" ||
                    unit === "GOLD" ||
                    unit === "USDC" ||
                    unit === "QUARK" ||
                    unit === "REDD" ||
                    unit === "SUSHI" ||
                    unit === "ORO" ||
                    unit === "ORB"
                        ? unit
                        : "USDC",
                amount: rewardAmount,
            },
            taskStatus,
            userRelation,
            isActionable,
            isPinned,
            // isPinned: false,
            pinLevel: "NONE",
            stwCanSpinNow,
            stwCanSpinAt,
            brCanCheckNow,
            brNextEligibleAt,
        };
    };

    const orderedItems = useMemo(
        () => [gameCatalogueFeedItem, ...feedState.items],
        [feedState.items, gameCatalogueFeedItem],
    );

    // Auto-reopen the drawer if we have a fresh resume payload
    useEffect(() => {
        const resume = readResume();
        if (!resume) return;
        const item = feedState.items.find((it) => it.itemId === resume.taskId);
        if (!item) return; // wait until feed is loaded; effect reruns as items change
        setSelectedTask(item);
        if (resume.t === "chi")
            setOpenBottomDrawer("TaskOverViewCheckInCampaign");
        else if (resume.t === "survey")
            setOpenBottomDrawer("TaskOverviewSurvey");
        else if (resume.t === "action")
            setOpenBottomDrawer("TaskOverviewAction");
        // clear immediately so we don't loop on future navigations
        clearResume();
    }, [feedState.items]);

    const taskList: z.infer<typeof TaskListItemSchema>[] = useMemo(
        () => orderedItems.map((it) => getTaskListItem(it as FeedItem)),
        [orderedItems],
    );

    return (
        <>
            <div className="main-page">
                <div className="main-page__top">
                    <MainTitle />
                    {!appSlice.loading?.appSession && <UserStatsTicker />}

                    {appSlice.loading?.appSession ? (
                        <div className="main-page__top__reward-activity-ticker-skeleton">
                            <Skeleton
                                baseColor="#ffffff"
                                highlightVariation={35}
                                borderRadius={"5vw"}
                                width={"100%"}
                                opacity={0.1}
                                className="main-page__top__reward-activity-ticker-skeleton__item"
                            />
                        </div>
                    ) : (
                        <RewardActivityTicker />
                    )}

                    {showXFollowButton && (
                        <button
                            className="main-page__top__x-btn"
                            onClick={() => {
                                window.open("https://x.com/pebbler_", "_blank");
                            }}
                        >
                            <div className="main-page__top__x-btn__inner">
                                <p>{t("MainPage.followOnX")}</p>
                                <XLogoSvg />
                            </div>
                        </button>
                    )}
                </div>

                <div className="main-page__bottom">
                    <CloudBlobsOverlay
                        className="main-page__bottom__overlay"
                        seed="main-bottom-10" // deterministic layout key
                        count={200} // density
                        lengthPct={[40, 60]} // variety in length
                        thicknessPct={[20, 60]} // variety in thickness
                        rotationDeg={[-70, 35]} // small tilt to feel organic
                        opacityRange={[0.4, 0.6]} // soft layering
                        blurPx={23.5} // cloud-like blur
                        areaPaddingPct={0} // keep away from edges
                        separationBias={0.45} // how strongly we avoid overlaps
                        maxAttemptsPerBlob={100} // patience while placing
                        verticalBandPct={[0, 100]} // full height of the blue area
                        cornerRadiusPct={0.1} // 0..1: how rounded the corners are
                        headTailThicknessDeltaPct={[0.225, 0.375]} // min/max taper % between head/tail
                    />

                    {/* <TaskListHeader /> */}

                    {/* <button
                        onClick={() => {
                            navigate("/referral-v2");
                        }}
                    >
                        NAV 2 REFERRAL V2
                    </button> */}

                    <TaskListHeader2
                        cardItems={[
                            {
                                id: "play-and-earn",
                                imageUrl: `https://media.bridge.cm/play-and-earn-texts/play-and-earn-card-img_${LANG_TO_URL_SUFFIX[i18n.language as LanguageCode] ?? "en"}.webp`,
                                onClick: () => {
                                    sendHapticFeedback({ type: "light" });
                                    markVisited("play-and-earn");
                                    navigate("/game", {
                                        state: { campaignId: "game" },
                                    });
                                },
                                cornerBadge: undefined,
                                notificationCount: visitedToday.has(
                                    "play-and-earn",
                                )
                                    ? undefined
                                    : 1,
                            },
                            {
                                id: "a-vs-b",
                                imageUrl:
                                    "https://media.bridge.cm/a-vs-b-card-img.webp",
                                onClick: () => {
                                    sendHapticFeedback({ type: "light" });
                                    markVisited("a-vs-b");
                                    navigate("/ab-page", {
                                        state: {
                                            campaignId:
                                                "04fca0e1-9928-419f-a320-e09b101d7a95",
                                        },
                                    });
                                },
                                cornerBadge: "NEW",
                                notificationCount: visitedToday.has("a-vs-b")
                                    ? undefined
                                    : 1,
                            },
                        ]}
                    />

                    <div className="main-page__bottom__banners">
                        {/* <ComingSoonV3Banner /> */}
                    </div>

                    <TaskList
                        onTaskClick={handleTaskPreviewClick}
                        tasks={taskList}
                    />
                </div>
            </div>

            <BottomDrawer
                isOpen={openBottomDrawer !== null}
                title={
                    selectedTask?.productType === "SURVEY"
                        ? "Survey"
                        : selectedTask?.productType === "PARTNER_CAMPAIGN_CHI"
                          ? "Check-in Campaign"
                          : selectedTask?.productType ===
                              "INTERNAL_CAMPAIGN_STW"
                            ? "Wheel Spin"
                            : selectedTask?.productType ===
                                "PARTNER_CAMPAIGN_ACTION"
                              ? "Task"
                              : "Activity"
                }
                canClose={true}
                onClose={() => {
                    setOpenBottomDrawer(null);
                    clearResume(); // user explicitly closed; don't auto-reopen
                }}
                contentStyling={{ padding: "9.09vw 0 9.09vw 0" }}
                includeHeader={false}
                className={`main-page__bottom-drawer ${
                    openBottomDrawer === "TaskOverViewCheckInCampaign"
                        ? "task-overview-check-in-campaign"
                        : openBottomDrawer === "TaskOverviewAction"
                          ? "task-overview-action"
                          : ""
                }`}
                withBackdrop={true}
            >
                {openBottomDrawer === "TaskOverviewSurvey" && (
                    <TaskOverviewSurvey
                        taskId={selectedTask?.itemId ?? ""}
                        onClickStart={handleTaskStartClick}
                        onClose={() => {
                            setOpenBottomDrawer(null);
                            clearResume();
                        }}
                    />
                )}
                {openBottomDrawer === "TaskOverViewCheckInCampaign" && (
                    <TaskOverViewCheckInCampaign
                        taskId={selectedTask?.itemId ?? ""}
                        onClose={() => {
                            setOpenBottomDrawer(null);
                            clearResume();
                        }}
                    />
                )}
                {openBottomDrawer === "TaskOverviewAction" && (
                    <TaskOverviewActionCampaign
                        taskId={selectedTask?.itemId ?? ""}
                        onClose={() => {
                            setOpenBottomDrawer(null);
                            clearResume();
                        }}
                    />
                )}
            </BottomDrawer>

            <Toaster />
        </>
    );
}
